import { useCallback } from 'react'
import log from 'loglevel'
import { DismissRequest } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { useShowConfirmationError } from 'src/Confirmation/ConfirmationContext'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { useAuthorizedGrpcClient } from '../../../adapter'
import { useRefreshControlPolicyLinks } from '../model/control/useControlMutations'

/**
 * useDeletePolicy deletes the policy and resolves to whether it did. Its controls stay,
 * unlinked from it and still linked to their other policies. A failure shows in the
 * confirmation dialog it is called from.
 * @param policyId  rowId
 */
export const useDeletePolicy = (policyId: string): (() => Promise<boolean>) => {
  const refreshControlPolicyLinks = useRefreshControlPolicyLinks()
  const policyClient = useAuthorizedGrpcClient(PolicyPromiseClient)
  const showError = useShowConfirmationError()

  return useCallback(async () => {
    try {
      await policyClient.dismiss(
        new DismissRequest().setId(policyId).setDismiss(true),
      )
    } catch (error) {
      const { message, shouldLog } = getErrorMessage(error)
      if (shouldLog) log.error('Error deleting policy', error)
      showError('Unable to Delete Policy', message)
      return false
    }
    try {
      await refreshControlPolicyLinks()
    } catch (error) {
      log.error('Error refreshing after deleting a policy', error)
    }
    return true
  }, [refreshControlPolicyLinks, policyClient, policyId, showError])
}
